import * as Sentry from "@sentry/browser";

import datadogLogger from "@lib/datadogLogger";
import { impersonationFetch } from "@lib/fetch/impersonationFetch";

import { DustLogBuffer } from "./DustLogBuffer";
import { DustSseEngine } from "./DustSseEngine";
import { EventCallbackManager } from "./EventCallbackManager";
import { MessageSequencer } from "./MessageSequencer";
import type { SequencerDiagnostics } from "./MessageSequencer";
import { consecutiveFetchFailureLimit, maxRetries as engineMaxRetries } from "./constants";
import { isDustUnreachableInTab, latchDustUnreachable, subscribeToDustUnreachable } from "./dustUnreachableLatch";
import type {
  DustConfigResponse,
  DustConnectionProbe,
  DustDashboardEventMessage,
  DustEngineState,
  DustEventCallback,
  DustMessage,
  DustProviderOptions,
  DustSseEngineLiveDiagnostics,
} from "./types";

/**
 * DustProvider manages a per-tab SSE connection to the DUST service for
 * real-time dashboard notifications.
 *
 * Key features:
 * - Establishes and maintains a per-tab SSE connection to DUST service
 * - Handles authentication via JWT tokens from /dust/auth
 * - Supports dashboard event subscriptions (via DashboardDustNotifier)
 * - Reuses MessageSequencer and EventCallbackManager for sequencing and routing
 * - Implements retry logic with exponential backoff (in DustSseEngine)
 * - Handles page lifecycle events (pagehide/pageshow for bfcache)
 */
export type DustConnectionStateChangeCallback = (isConnected: boolean) => void;

export interface DustConnectionDiagnostics {
  connected: boolean;
  retryCount: number;
  maxRetries: number;
  hasValidConfig: boolean;
  isConfigExpired: boolean;
  activeSubscriptions: number;
  isRetrying: boolean;
  isPageUnloading: boolean;
  dustHost: string | null;
  mite: string | null;
  isOnline: boolean;
  connectionLifetimeMs: number | null;
  msUntilNextReconnect: number | null;
  reconnectionCount: number;
  /**
   * Full engine snapshot (heartbeat/cir/connection-start timings, DDR history,
   * readyState, HTTP status, latency, reconnect counters). Surfaced so
   * streaming-timeout reports carry the connection telemetry.
   */
  engineState?: DustEngineState;
  lastConnectionError?: string;
  connectionProbe?: DustConnectionProbe;
  wasEverConnected: boolean;
  /**
   * Recent connection-relevant provider + engine logs, oldest first, byte-capped.
   * Surfaced for connection debugging via Sentry `extra.dust`.
   */
  recentLogs: string[];
}

export class DustProvider {
  private mite: string | null = null;
  private dustHost: string | null = null;
  private auth: string | null = null;
  private expiration: number | null = null;
  private pageHideHandler: (() => void) | null = null;
  private pageShowHandler: ((event: PageTransitionEvent) => void) | null = null;
  private beforeUnloadHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;
  private freezeHandler: (() => void) | null = null;
  private resumeHandler: (() => void) | null = null;
  private isPageUnloading = false;
  private isDevOrStaging: boolean;
  private onConfigRefreshNeeded?: () => void;

  private activeSubscriptions = 0;

  // The per-tab SSE connection runs over the shared DustSseEngine, which owns
  // the reliability core (heartbeat watchdog, gapless TTL reconnect, DDR, 503
  // jitter, health probe, HTTP-status visibility). Created lazily on first
  // subscription.
  private engine: DustSseEngine | null = null;
  private lastEngineError: string | null = null;
  private lastEngineProbe: DustConnectionProbe | null = null;
  private unsubscribeFromUnreachableLatch: (() => void) | null = null;

  private messageSequencer: MessageSequencer;
  private eventCallbackManager: EventCallbackManager;

  private readonly channelName = "dust-events";

  // Connection-relevant log timeline for diagnostics. This same buffer is
  // injected into the engine so provider + engine logs interleave
  // chronologically.
  private readonly logBuffer = new DustLogBuffer();

  private connected = false;
  private wasEverConnected = false;
  private connectionStateCallbacks: Set<DustConnectionStateChangeCallback> = new Set();

  // Connection lifetime tracking for diagnostics (set on connect, cleared on
  // disconnect). The engine owns reconnect bookkeeping.
  private connectionOpenedAt: number | null = null;

  constructor(options: DustProviderOptions) {
    this.isDevOrStaging = options.isDevOrStaging;
    this.onConfigRefreshNeeded = options.onConfigRefreshNeeded;

    this.log("DUST: Using direct EventSource mode");

    this.unsubscribeFromUnreachableLatch = subscribeToDustUnreachable(() => this.handleUnreachableLatchChange());

    this.eventCallbackManager = new EventCallbackManager();
    this.messageSequencer = new MessageSequencer({
      onMessageProcessed: (data) => {
        this.eventCallbackManager.processMessage(this.channelName, data);
      },
      isDevOrStaging: this.isDevOrStaging,
    });
  }

  /**
   * Record a connection-relevant log line into the diagnostics buffer (always)
   * and mirror it to the console when in dev/staging. The message is expected to
   * carry the existing `DUST: ` prefix; the buffer serializes any extra args.
   *
   * @param message Full log message (including the `DUST: ` prefix).
   * @param args Extra args to serialize and log.
   */
  private log(message: string, ...args: unknown[]): void {
    this.logBuffer.record(message, args);
    if (this.isDevOrStaging) {
      console.log(message, ...args);
    }
  }

  private logWarn(message: string, ...args: unknown[]): void {
    this.logBuffer.record(message, args);
    if (this.isDevOrStaging) {
      console.warn(message, ...args);
    }
  }

  private logError(message: string, ...args: unknown[]): void {
    this.logBuffer.record(message, args);
    if (this.isDevOrStaging) {
      console.error(message, ...args);
    }
  }

  /**
   * Console-only log for high-volume per-message events. Deliberately NOT
   * recorded into the diagnostics buffer to avoid spam.
   *
   * @param message Full log message (including the `DUST: ` prefix).
   * @param args Extra args.
   */
  private logVerbose(message: string, ...args: unknown[]): void {
    if (this.isDevOrStaging) {
      console.log(message, ...args);
    }
  }

  /**
   * Lazily create the SSE engine and wire its callbacks. The engine owns the
   * connection, reliability, and reconnect bookkeeping; this provider just
   * forwards events to the sequencer and surfaces connection state.
   */
  private ensureEngine(): DustSseEngine {
    if (this.engine) {
      return this.engine;
    }

    this.engine = new DustSseEngine({
      isDevOrStaging: () => this.isDevOrStaging,
      logPrefix: "[DUST Direct]",
      // Main-thread mode runs in the DOM, so use the impersonation-aware fetch
      // wrapper (confirmation modal for production-data writes while
      // impersonating).
      fetch: impersonationFetch,
      // Share this provider's buffer so engine logs interleave with provider
      // logs in a single chronological diagnostics timeline.
      logBuffer: this.logBuffer,
      callbacks: {
        onEvent: (message) => this.processDustMessage(message),
        onConnected: () => {
          this.log("DUST: Personal connection established");
          Sentry.addBreadcrumb({
            category: "dust",
            message: "Personal connection established",
            level: "info",
          });
          this.lastEngineError = null;
          this.lastEngineProbe = null;
          this.connectionOpenedAt = Date.now();
          this.setConnected(true);
        },
        onDisconnected: () => {
          this.log("DUST: Connection closed");
          this.connectionOpenedAt = null;
          this.setConnected(false);
        },
        onError: (error, probe) => {
          this.lastEngineError = error;
          if (probe) {
            this.lastEngineProbe = probe;
          }
          this.logError(`DUST: Connection error: ${error}`);
          Sentry.addBreadcrumb({
            category: "dust",
            message: `Connection error: ${error}`,
            level: "error",
          });
          if (navigator.onLine) {
            Sentry.captureMessage(`DUST direct connection error: ${error}`, {
              level: "error",
              tags: { component: "DustProvider", transport: "dust-direct" },
              extra: { dust: this.getConnectionDiagnostics() },
            });
          }
        },
        onAuthError: (status, reason) => {
          this.logError(`DUST: Auth error HTTP ${status} — ${reason ?? "unknown"}`);
        },
        onFetchFailureStreak: (consecutiveCount) => this.handleFetchFailureStreak(consecutiveCount),
        onConfigRefreshNeeded: () => {
          this.onConfigRefreshNeeded?.();
        },
      },
    });

    // A tab that latched before this engine existed — most often one that
    // reloaded while the verdict still stands — starts out suspended, so a
    // reload does not buy DUST a fresh round of connection attempts.
    if (isDustUnreachableInTab()) {
      this.engine.suspend();
    }

    return this.engine;
  }

  /**
   * React to this tab's DUST-unreachable verdict being set or expiring.
   *
   * Once the tab concludes DUST is unreachable there is nothing to gain by
   * continuing to open connections: every fetch-level failure resets the
   * backoff, so the engine would otherwise sit in a ~1–3s retry loop against a
   * dead endpoint for as long as the tab stays open. Suspending parks it until
   * the verdict expires, which is also the only way DUST wins the tab back.
   *
   * Suspending is safe to do from inside the engine callback that trips the
   * latch: the engine checks the flag at every connection entry point, so the
   * retry that callback goes on to schedule is skipped.
   */
  private handleUnreachableLatchChange(): void {
    if (isDustUnreachableInTab()) {
      this.engine?.suspend();
      return;
    }

    this.log("DUST: Unreachable verdict expired, trying DUST again");
    this.engine?.resume();

    if (this.activeSubscriptions > 0) {
      // Credentials have almost certainly expired over the life of the verdict,
      // so ask the host to refresh them; ensureConnection covers the case where
      // they are somehow still valid and no refresh lands.
      this.onConfigRefreshNeeded?.();
      this.engine?.ensureConnection("unreachable-verdict-expired");
    }
  }

  /**
   * Give up on DUST for this tab once fetch-level failures pile up with no
   * successful connection in between. See dustUnreachableLatch for why this
   * particular failure is treated as terminal rather than retryable.
   *
   * An offline tab produces the same symptom for an entirely recoverable
   * reason, so failures counted while the browser reports no connectivity are
   * ignored — the engine's own streak keeps climbing and trips on the next
   * failure that happens while online, and it clears the streak outright when
   * connectivity returns.
   *
   * Tripping the latch also suspends the engine (see
   * handleUnreachableLatchChange), so the tab stops connecting entirely rather
   * than retrying a dead endpoint every couple of seconds. Subscribers with no
   * Pusher fallback lose realtime for the life of the verdict, but they were
   * already receiving nothing — the connection is down, not slow.
   *
   * @param consecutiveCount Consecutive fetch-level failures reported by the engine.
   */
  private handleFetchFailureStreak(consecutiveCount: number): void {
    if (consecutiveCount < consecutiveFetchFailureLimit) {
      return;
    }

    if (typeof navigator !== "undefined" && !navigator.onLine) {
      return;
    }

    if (isDustUnreachableInTab()) {
      return;
    }

    this.logError(
      `DUST: ${consecutiveCount} consecutive fetch-level failures — treating DUST as unreachable for this tab`
    );
    latchDustUnreachable();
    datadogLogger.error("DUST unreachable in this tab, falling back to Pusher", {
      component: "DustProvider",
      transport: "dust-direct",
      dust: this.getConnectionDiagnostics(),
    });
  }

  /**
   * Returns whether the DUST connection is currently established.
   */
  isConnected(): boolean {
    return this.connected;
  }

  /**
   * Returns MessageSequencer diagnostics: per-stream sequence state, pending buffers, dedup stats.
   * Useful for debugging streaming-timeouts caused by sequencing issues.
   */
  getSequencerDiagnostics(): SequencerDiagnostics {
    return this.messageSequencer.getDiagnostics();
  }

  /**
   * Returns the log of received messaging sequences per stream_id (dev/staging only).
   */
  getReceivedSequenceLog(): Record<string, number[]> | null {
    return this.messageSequencer.getReceivedSequenceLog();
  }

  /**
   * Clears state for a completed stream to prevent memory leaks.
   */
  cleanupStream(streamId: string): void {
    this.messageSequencer.cleanupStream(streamId);
  }

  /**
   * Live DustSseEngine timing/counter fields for the current connection.
   */
  getLiveDiagnostics(): DustSseEngineLiveDiagnostics | null {
    return this.engine?.getLiveDiagnostics() ?? null;
  }

  /**
   * Returns a snapshot of DUST connection state for diagnostic logging.
   * Intended for inclusion in error reports (Sentry, Datadog) to disambiguate
   * whether DUST connectivity is the root cause of operator streaming failures.
   */
  getConnectionDiagnostics(): DustConnectionDiagnostics {
    const engineState = this.engine?.getState();

    const now = Date.now();

    return {
      connected: this.connected,
      retryCount: engineState?.retryCount ?? 0,
      maxRetries: engineState?.maxRetries ?? engineMaxRetries,
      hasValidConfig: this.hasValidConfig(),
      isConfigExpired: this.isConfigExpired(),
      activeSubscriptions: this.activeSubscriptions,
      isRetrying: engineState?.isRetrying ?? false,
      isPageUnloading: this.isPageUnloading,
      dustHost: this.dustHost,
      mite: this.mite || null,
      isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
      connectionLifetimeMs: this.connectionOpenedAt !== null ? now - this.connectionOpenedAt : null,
      // The engine owns reconnect scheduling and does not expose an absolute
      // next-reconnect time.
      msUntilNextReconnect: null,
      reconnectionCount: engineState?.reconnectionCount ?? 0,
      wasEverConnected: this.wasEverConnected,
      recentLogs: this.logBuffer.toArray(),
      ...(engineState ? { engineState } : {}),
      ...(this.lastEngineError ? { lastConnectionError: this.lastEngineError } : {}),
      ...(this.lastEngineProbe ? { connectionProbe: this.lastEngineProbe } : {}),
    };
  }

  /**
   * Drop the current SSE connection and open a fresh one (gapless when
   * possible). Intended for callers that detect streaming silence (e.g. the
   * Operator streaming-timeout) on a connection the engine still considers
   * healthy.
   */
  forceReconnect(): void {
    this.engine?.forceReconnect();
  }

  /**
   * Subscribe to connection state changes.
   * Returns an unsubscribe function.
   */
  onConnectionStateChange(callback: DustConnectionStateChangeCallback): () => void {
    this.connectionStateCallbacks.add(callback);
    return () => {
      this.connectionStateCallbacks.delete(callback);
    };
  }

  private setConnected(value: boolean): void {
    if (this.connected !== value) {
      this.connected = value;
      if (value) {
        this.wasEverConnected = true;
      }
      this.connectionStateCallbacks.forEach((cb) => cb(value));
    }
  }

  updateConfig(config: DustConfigResponse): void {
    if (config.mite && config.host && config.auth) {
      const hasChanges =
        this.mite !== config.mite ||
        this.dustHost !== config.host ||
        this.auth !== config.auth ||
        this.expiration !== (config.expiration || null);

      if (!hasChanges) {
        return;
      }

      const authChanged = this.auth !== config.auth;

      this.mite = config.mite;
      this.dustHost = config.host;
      this.auth = config.auth;
      this.expiration = config.expiration || null;

      this.log("DUST config updated", {
        mite: this.mite,
        host: this.dustHost,
        expiration: this.expiration,
      });

      // After auth refresh (e.g. 401/403 triggered config_refresh_needed),
      // push the new credentials to the engine so it can reconnect. Without
      // this, updateConfig only stores tokens locally — the engine never
      // receives them because startSubscription() is not called again for
      // auth-only changes.
      if (authChanged && this.activeSubscriptions > 0 && this.engine) {
        // Engine keeps its own config copy; setConfigAndConnect updates it and
        // only opens a connection when one isn't already live, so the refreshed
        // auth is picked up on the next (gapless) reconnect.
        this.engine.setConfigAndConnect({
          mite: this.mite!,
          dustHost: this.dustHost!,
          auth: this.auth!,
          expiration: this.expiration || 0,
        });
      }
    }
  }

  hasValidConfig(): boolean {
    return Boolean(this.mite && this.dustHost && this.auth);
  }

  isConfigExpired(): boolean {
    if (!this.expiration) {
      return false;
    }
    // Math.floor(Date.now() / 1000) instead of getNowTimestamp — @lib must not depend on @services.
    return Math.floor(Date.now() / 1000) > this.expiration - 30;
  }

  /**
   * Set the callback to be invoked when config needs to be refreshed.
   * This is useful for singleton patterns where the callback may need to be
   * updated after initial creation.
   */
  setOnConfigRefreshNeeded(callback: () => void): void {
    this.onConfigRefreshNeeded = callback;
  }

  setupPageLifecycleHandlers(): void {
    if (this.pageHideHandler || this.pageShowHandler) {
      return;
    }

    this.beforeUnloadHandler = () => {
      this.isPageUnloading = true;
    };
    window.addEventListener("beforeunload", this.beforeUnloadHandler);

    this.pageHideHandler = () => {
      this.isPageUnloading = true;
      if (this.engine) {
        this.log("DUST: Page hidden, closing direct connection gracefully");
        // Close (release the lease) without decrementing the subscription ref
        // count so a bfcache restore can reconnect the same logical subscription.
        this.engine.close();
      }
    };
    window.addEventListener("pagehide", this.pageHideHandler);

    this.pageShowHandler = (event: PageTransitionEvent) => {
      if (event.persisted) {
        this.isPageUnloading = false;
        this.log("DUST: Page restored from bfcache, reconnecting");
        this.onConfigRefreshNeeded?.();
        if (this.activeSubscriptions > 0) {
          this.engine?.ensureConnection("bfcache-restore");
        }
      }
    };
    window.addEventListener("pageshow", this.pageShowHandler);

    this.offlineHandler = () => {
      this.log("DUST: Network offline, closing direct connection");
      this.engine?.close();
    };
    window.addEventListener("offline", this.offlineHandler);

    this.onlineHandler = () => {
      this.log("DUST: Network online, ensuring connection");
      if (this.activeSubscriptions > 0) {
        this.engine?.ensureConnection("online");
      }
    };
    window.addEventListener("online", this.onlineHandler);

    // Page Lifecycle API: a backgrounded tab can be frozen (CPU suspended) and
    // later resumed. On resume the SSE connection is almost certainly
    // dead-but-silent, so proactively verify/restart it rather than waiting up
    // to the full heartbeat-watchdog window.
    this.freezeHandler = () => {
      this.log("DUST: Tab frozen, closing direct connection");
      this.engine?.close();
    };
    document.addEventListener("freeze", this.freezeHandler);

    this.resumeHandler = () => {
      this.log("DUST: Tab resumed, ensuring connection");
      if (this.activeSubscriptions > 0) {
        this.engine?.ensureConnection("resume");
      }
    };
    document.addEventListener("resume", this.resumeHandler);
  }

  /**
   * Subscribe to a specific event name.
   */
  subscribeToEvent(eventName: string, callback: DustEventCallback): () => void {
    this.eventCallbackManager.addCallback({
      channelName: this.channelName,
      eventName,
      callback,
    });

    return () => {
      this.eventCallbackManager.removeCallback({
        channelName: this.channelName,
        eventName,
        callback,
      });
    };
  }

  /** Subscribe with last-writer-wins dispatch; unsubscribing restores the previous subscriber. */
  subscribeToEventExclusive(eventName: string, callback: DustEventCallback): () => void {
    this.eventCallbackManager.setExclusiveCallback({
      channelName: this.channelName,
      eventName,
      callback,
    });

    return () => {
      this.eventCallbackManager.removeExclusiveCallback({
        channelName: this.channelName,
        eventName,
        callback,
      });
    };
  }

  startSubscription(): void {
    this.activeSubscriptions++;

    if (!this.hasValidConfig()) {
      this.logWarn("DUST: Cannot start subscription - missing config");
      return;
    }

    this.isPageUnloading = false;
    this.ensureEngine().setConfigAndConnect({
      mite: this.mite!,
      dustHost: this.dustHost!,
      auth: this.auth!,
      expiration: this.expiration || 0,
    });
  }

  endSubscription(): void {
    this.activeSubscriptions = Math.max(0, this.activeSubscriptions - 1);

    if (this.activeSubscriptions > 0) {
      this.log(`DUST: Subscription ended, ${this.activeSubscriptions} active subscriptions remaining`);
      return;
    }

    // Close the connection (cancels all engine timers) but keep the engine's
    // config so a later startSubscription can reconnect.
    this.engine?.close();
  }

  /**
   * Route an already-parsed DUST message. Control messages (ddr/ttl) are owned
   * by the engine, so only dashboard events reach the provider here.
   */
  private processDustMessage(message: DustMessage): void {
    if (!message?.type) {
      this.logError("DUST: Received message without type", message);
      return;
    }

    this.logVerbose(`DUST: Received message of type '${message.type}'`, message);

    if (message.type === "evt") {
      const evtMessage = message as DustDashboardEventMessage;
      const { event, payload } = evtMessage.body;

      this.messageSequencer.processMessage({
        event,
        stream_id: payload?.stream_id as string | undefined,
        sequence: payload?.sequence as number | undefined,
        message_id: payload?.message_id as string | undefined,
        payload,
      });
      return;
    }

    this.logVerbose(`DUST: Unhandled message type '${(message as DustMessage).type}'`);
  }

  destroy(): void {
    this.activeSubscriptions = 0;
    this.endSubscription();
    this.unsubscribeFromUnreachableLatch?.();
    this.unsubscribeFromUnreachableLatch = null;
    if (this.engine) {
      this.engine.reset();
      this.engine = null;
    }
    this.mite = null;
    this.dustHost = null;
    this.auth = null;
    this.lastEngineError = null;
    this.lastEngineProbe = null;
    this.connectionOpenedAt = null;
    this.logBuffer.clear();
    this.setConnected(false);
    this.connectionStateCallbacks.clear();

    if (this.beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
    if (this.offlineHandler) {
      window.removeEventListener("offline", this.offlineHandler);
      this.offlineHandler = null;
    }
    if (this.onlineHandler) {
      window.removeEventListener("online", this.onlineHandler);
      this.onlineHandler = null;
    }
    if (this.freezeHandler) {
      document.removeEventListener("freeze", this.freezeHandler);
      this.freezeHandler = null;
    }
    if (this.resumeHandler) {
      document.removeEventListener("resume", this.resumeHandler);
      this.resumeHandler = null;
    }
    if (this.pageHideHandler) {
      window.removeEventListener("pagehide", this.pageHideHandler);
      this.pageHideHandler = null;
    }
    if (this.pageShowHandler) {
      window.removeEventListener("pageshow", this.pageShowHandler);
      this.pageShowHandler = null;
    }
  }
}
